import React, { createContext, useState, useEffect, ReactNode } from 'react'
import { useSearchParams } from 'react-router-dom'

interface HospitalContextType {
    selectedHospitalIds: string[]
    setSelectedHospitalIds: (ids: string[]) => void
    isMultiSelect: boolean
    refreshKey: number
    triggerRefresh: () => void
}

// The HospitalId query param is authoritative -- RequireAuth guarantees it holds exactly the
// sites the user may view. Parse it (tolerating the legacy "(a,b)" wrapper) into clean ids.
function parseUrlHospitalIds(raw: string | null): string[] {
    if (!raw) return []
    return raw.replace(/^\(|\)$/g, '').split(',').map((s) => s.trim()).filter(Boolean)
}

// The selection to use for a given set of authorized (URL) ids: keep a previously-stored
// subset if it's still valid, otherwise default to the first authorized site so the dashboard
// has a selection -- and therefore data -- immediately, without waiting on the HospitalSelector
// hospital-list fetch (mirrors that component's default).
function seedSelection(urlIds: string[]): string[] {
    if (urlIds.length === 0) return []
    let stored: unknown = []
    try {
        const raw = sessionStorage.getItem('selectedHospitalIds')
        stored = raw ? JSON.parse(raw) : []
    } catch {
        stored = []
    }
    const validStored = Array.isArray(stored) ? stored.filter((id) => urlIds.includes(id)) : []
    return validStored.length > 0 ? validStored : [urlIds[0]]
}

export const HospitalContext = createContext<HospitalContextType>({
    selectedHospitalIds: [],
    setSelectedHospitalIds: () => { },
    isMultiSelect: false,
    refreshKey: 0,
    triggerRefresh: () => { },
})

interface HospitalProviderProps {
    children: ReactNode
}

export const HospitalProvider: React.FC<HospitalProviderProps> = ({ children }) => {
    const [searchParams] = useSearchParams()
    const urlHospitalId = searchParams.get('HospitalId') || ''

    const [selectedHospitalIds, setSelectedHospitalIdsInternal] = useState<string[]>(
        () => seedSelection(parseUrlHospitalIds(urlHospitalId))
    )

    // Keep the selection anchored to the authoritative URL param. When it appears/changes
    // (e.g. right after login), seed from it so the dashboard loads data immediately; when it
    // is absent, clear. A valid current subset survives (so a user-chosen subset persists
    // across navigation, which preserves the param but not the selection).
    useEffect(() => {
        const urlIds = parseUrlHospitalIds(urlHospitalId)
        if (urlIds.length === 0) {
            setSelectedHospitalIdsInternal([])
            sessionStorage.removeItem('selectedHospitalIds')
            sessionStorage.removeItem('hospitalNames')
            return
        }
        setSelectedHospitalIdsInternal((prev) => {
            const validPrev = prev.filter((id) => urlIds.includes(id))
            return validPrev.length > 0 ? validPrev : seedSelection(urlIds)
        })
    }, [urlHospitalId])

    const [refreshKey, setRefreshKey] = useState(0)
    const isMultiSelect = selectedHospitalIds.length > 1

    const triggerRefresh = React.useCallback(() => {
        setRefreshKey(prev => prev + 1)
    }, [])

    const setSelectedHospitalIds = React.useCallback((ids: string[]) => {
        setSelectedHospitalIdsInternal(ids)
    }, [])

    useEffect(() => {
        if (selectedHospitalIds.length > 0) {
            sessionStorage.setItem('selectedHospitalIds', JSON.stringify(selectedHospitalIds))
            sessionStorage.setItem('isMultiSelect', JSON.stringify(isMultiSelect))
        }
    }, [selectedHospitalIds, isMultiSelect])

    useEffect(() => {
        const handleStorageEvent = (e: StorageEvent) => {
            if (e.key === 'selectedHospitalIds' && e.newValue) {
                try {
                    const ids = JSON.parse(e.newValue)
                    if (Array.isArray(ids) && ids.length > 0) {
                        setSelectedHospitalIdsInternal(ids)
                    }
                } catch {
                }
            }
        }

        const handleMessage = (event: MessageEvent) => {
            if (event.data && event.data.hospitalIds) {
                const ids = event.data.hospitalIds
                if (Array.isArray(ids) && ids.length > 0) {
                    setSelectedHospitalIdsInternal(ids)
                }
            }
        }

        window.addEventListener('storage', handleStorageEvent)
        window.addEventListener('message', handleMessage)

        return () => {
            window.removeEventListener('storage', handleStorageEvent)
            window.removeEventListener('message', handleMessage)
        }
    }, [])

    return (
        <HospitalContext.Provider
            value={{ selectedHospitalIds, setSelectedHospitalIds, isMultiSelect, refreshKey, triggerRefresh }}
        >
            {children}
        </HospitalContext.Provider>
    )
}
